import { toDecimalPoint } from '@round/utils';
import { ReactNode, useState } from 'react';
import { Breakdown, assets } from '@round/whitelabel-ui';
import styles from './FeeSetBreakdown.module.css';
import { ButtonShell, Skeleton, SkeletonizedValue } from '@round/ui-kit';
import cn from 'classnames';
import { useCurrencies } from 'contexts/OptionsContext/useCurrencies';
import { InfoTooltip } from '@round/whitelabel-ui';
import { creatorbase } from '@round/api';
import { Tooltip } from '@round/whitelabel-ui';

const { ChevronRight: ChevronRightIcon, ExchangeCoin: ExchangeRateCoinIcon } = assets;

export type FeeItem = {
    label: string;
    amount: number | null | undefined;
    percentageOfCost: number | null | undefined;
    description?: ReactNode;
};

const getLabel = (name: string) => {
    return name
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

const getBaseFeeTooltip = (label: string) => {
    const baseFeeTooltips = {
        'Payment Processing Fee':
            'Variable fee charged by payment processor depending on supplier info (e.g. location / payment preferences / bank charges etc).',
        'Platform Fee': 'Fixed fee charged by platform.',
    };

    return baseFeeTooltips[label as keyof typeof baseFeeTooltips] || undefined;
};

export const mapFeeListToFeeItems = (
    feeList: creatorbase.FeeListItem[] | undefined,
    originalCost: number | null | undefined
): FeeItem[] => {
    if (!feeList) {
        return [];
    }

    return feeList.map((fee) => {
        const baseFeeTooltip = getBaseFeeTooltip(getLabel(fee.name));
        const percentageOfCost = originalCost && fee.value ? toDecimalPoint((fee.value / originalCost) * 100, 2) : null;
        return {
            label: getLabel(fee.name),
            amount: fee.value,
            percentageOfCost: percentageOfCost,
            description: fee?.description || baseFeeTooltip,
        };
    });
};

type Props = {
    label: string;
    feeSet: FeeItem[];
    baseCost: number | null | undefined;
    total: number | null | undefined;
    currencyId: number | undefined;
    isPending: boolean;
    isFetching: boolean;
    baseCurrencyId: number | undefined;
    exchangeRate: number | null | undefined;
    vatAmount?: number;
    vatRate?: number;
};

const FeeSetBreakdown = ({
    label,
    feeSet,
    baseCost,
    total,
    currencyId,
    isPending,
    isFetching,
    baseCurrencyId,
    exchangeRate,
    vatAmount,
    vatRate,
}: Props) => {
    const { getCurrencyById } = useCurrencies();
    const currency = getCurrencyById(currencyId);

    const [isExpanded, setIsExpanded] = useState(false);
    const hasFeeSet = feeSet.length > 0;
    const showExpanded = isExpanded && hasFeeSet;

    return isPending ? (
        <Skeleton height="4.375rem" />
    ) : (
        <Breakdown.Container>
            {showExpanded && (
                <>
                    <Breakdown.Item>
                        <Breakdown.Label>Creator Fee</Breakdown.Label>
                        <Breakdown.Amount
                            amount={baseCost}
                            vatAmount={vatAmount}
                            vatRate={vatRate}
                            currency={currency}
                            isLoading={isFetching}
                        />
                    </Breakdown.Item>
                    <Breakdown.Separator />

                    {feeSet.map((item) => (
                        <Breakdown.Item key={item.label}>
                            <Breakdown.Label>
                                <span>{item.label}</span>
                                {(item.description || item.percentageOfCost) && (
                                    <InfoTooltip className={styles.feeInfoTooltip}>
                                        <SkeletonizedValue isInitialized={!isFetching} width="8rem">
                                            {item.description || `${item.percentageOfCost}% of post cost`}
                                        </SkeletonizedValue>
                                    </InfoTooltip>
                                )}
                            </Breakdown.Label>
                            <Breakdown.Amount amount={item.amount} currency={currency} isLoading={isFetching} />
                        </Breakdown.Item>
                    ))}
                    <Breakdown.Separator />
                </>
            )}

            <Breakdown.Item isTotal>
                <Breakdown.Label>
                    <ButtonShell
                        onClick={() => setIsExpanded(!isExpanded)}
                        disabled={!hasFeeSet}
                        type="button"
                        className={cn(styles.expandLabelButton, { [styles.disabled]: !hasFeeSet })}
                    >
                        <ChevronRightIcon
                            className={cn(styles.chevron, {
                                [styles.rotated]: showExpanded,
                                [styles.disabled]: !hasFeeSet,
                            })}
                        />
                        <span>{label}</span>
                        {!showExpanded && hasFeeSet && <InfoTooltip>Includes estimated fees</InfoTooltip>}
                    </ButtonShell>
                </Breakdown.Label>
                <Tooltip.Root>
                    {(anchor, setAnchorRef) => (
                        <>
                            <ExchangeRateCoinIcon
                                ref={setAnchorRef}
                                className={cn(styles.exchangeRateIcon, {
                                    [styles.invisible]: !exchangeRate || isPending,
                                })}
                            />
                            <Tooltip anchorElement={anchor} disabled={!exchangeRate}>
                                <Tooltip.Title>Est. Exchange Rate</Tooltip.Title>
                                <Tooltip.Body>
                                    <SkeletonizedValue isInitialized={!isFetching} width="8rem">
                                        {`1 ${getCurrencyById(baseCurrencyId)
                                            ?.name} : ${exchangeRate} ${currency?.name}`}
                                    </SkeletonizedValue>
                                </Tooltip.Body>
                            </Tooltip>
                        </>
                    )}
                </Tooltip.Root>
                <Breakdown.Amount
                    amount={total}
                    vatAmount={vatAmount}
                    vatRate={vatRate}
                    currency={currency}
                    isLoading={isFetching}
                />
            </Breakdown.Item>
        </Breakdown.Container>
    );
};

export default FeeSetBreakdown;
